// frontend/src/components/ArtHouse/MoreByArtistRail.jsx
// DeviantArt-style rail (refined)
// - Flat header: "More by X"
// - Right action: "View Profile" (real link)
// - 3×3 grid (max 9)
// - Minimal, artsy, non-flashy
// FIX: clicking a tile ALWAYS navigates to /arthouse/artwork/:id

import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { fetchArtistProfile, fetchArtworks } from "../../services/arthouseApi";

function safeText(v, fallback = "") {
  return (v ?? "").toString().trim() || fallback;
}

function getArtworkId(a) {
  return a?.id ?? a?.artwork_id ?? a?.artworkId ?? null;
}

// EXACT match with ArtistProfilePage grid fields
function getGridImage(a) {
  return (
    a?.image_url ||
    a?.imageUrl ||
    a?.thumbnail_url ||
    a?.thumb_url ||
    a?.cover_url ||
    a?.media_url ||
    ""
  );
}

function getArtistIdFromArtwork(a) {
  const artist = a?.artist || a?.creator || null;
  return artist?.id ?? artist?.artist_id ?? artist?.artistId ?? null;
}

export default function MoreByArtistRail({
  artistId,
  artistName = "Artist",
  currentArtworkId,
  className = "",
  maxItems = 9, // fixed to 9 (3x3)
  railCardClassName = "rounded-xl border border-white/10 bg-black/30",
}) {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;

    async function load() {
      if (!artistId) {
        setItems([]);
        return;
      }

      setLoading(true);

      try {
        // Primary source (same as ArtistProfilePage)
        const profile = await fetchArtistProfile(artistId);
        const list = Array.isArray(profile?.artworks) ? profile.artworks : [];

        const filtered = list
          .filter((a) => {
            const aid = getArtworkId(a);
            return aid && String(aid) !== String(currentArtworkId);
          })
          .slice(0, maxItems);

        if (!alive) return;

        if (filtered.length) {
          setItems(filtered);
          return;
        }

        // Fallback only if profile has nothing
        const fallback = await fetchArtworks({
          q: "",
          sort: "new",
          page: 1,
          limit: 120,
        });

        const all = Array.isArray(fallback?.artworks)
          ? fallback.artworks
          : Array.isArray(fallback)
          ? fallback
          : [];

        const filteredFallback = all
          .filter((a) => {
            const aid = getArtworkId(a);
            if (!aid || String(aid) === String(currentArtworkId)) return false;

            const aArtistId =
              getArtistIdFromArtwork(a) ??
              a?.artist_id ??
              a?.artistId ??
              a?.creator_id ??
              a?.creatorId ??
              null;

            return String(aArtistId ?? "") === String(artistId);
          })
          .slice(0, maxItems);

        if (!alive) return;
        setItems(filteredFallback);
      } catch {
        if (!alive) return;
        setItems([]);
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [artistId, currentArtworkId, maxItems]);

  function openArtwork(a) {
    const id = getArtworkId(a);
    if (!id) return;
    nav(`/arthouse/art/${encodeURIComponent(String(id))}`, {
      state: { artwork: a },
    });
  }

  return (
    <section className={`${railCardClassName} ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-3">
        <div className="text-sm font-extrabold text-white/90 truncate">
          More by {artistName}
        </div>

        {artistId ? (
          <Link
            to={`/arthouse/artist/${encodeURIComponent(String(artistId))}`}
            className="
              text-sm font-semibold text-emerald-300
              hover:text-emerald-200 transition
            "
          >
            View Profile
          </Link>
        ) : null}
      </div>

      {/* Grid */}
      <div className="px-4 pb-4 pt-3">
        {loading ? (
          <div className="grid grid-cols-3 gap-3">
            {Array.from({ length: maxItems }).map((_, i) => (
              <div
                key={i}
                className="aspect-square rounded-md border border-white/10 bg-white/[0.04] animate-pulse"
              />
            ))}
          </div>
        ) : items.length ? (
          <div className="grid grid-cols-3 gap-3">
            {items.map((a, idx) => {
              const id = getArtworkId(a);
              const img = getGridImage(a);
              const title = safeText(a?.title, "Untitled");
              const disabled = !id;

              return (
                <button
                  key={String(id ?? idx)}
                  type="button"
                  onClick={() => openArtwork(a)}
                  disabled={disabled}
                  className="
                    group block text-left overflow-hidden rounded-md
                    border border-white/10 bg-black/30
                    hover:border-white/25 transition
                    disabled:opacity-50 disabled:cursor-not-allowed
                    focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/25
                  "
                  title={disabled ? "Missing artwork id" : title}
                >
                  <div className="relative aspect-square">
                    {img ? (
                      <img
                        src={img}
                        alt={title}
                        className="absolute inset-0 h-full w-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="absolute inset-0 grid place-items-center text-[11px] text-white/50">
                        No image
                      </div>
                    )}

                    {/* subtle DA-style hover darken */}
                    <div className="pointer-events-none absolute inset-0 bg-black/0 group-hover:bg-black/20 transition" />
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="rounded-lg border border-white/10 bg-white/[0.03] p-4 text-sm text-white/60">
            No more artworks found for this artist yet.
          </div>
        )}
      </div>
    </section>
  );
}
